// Per-handle avatar — the design's lookup-from-fixed-triad table doesn't
// translate to Loom (we have arbitrary GitLab handles), so the background
// hue + the initials are both derived deterministically from the handle.

import type { JSX } from "react";

import { Icon } from "./Icon";

interface AvatarProps {
  readonly handle: string | null;
  readonly size?: number;
}

// FNV-1a-ish 32-bit hash. Stable and cheap; we only need ~360 buckets.
const hashCode = (s: string): number => {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};

const hueFor = (handle: string): number => hashCode(handle) % 360;

const initialsFor = (handle: string): string => {
  // GitLab handles are lowercase ASCII; show the first two chars uppercased.
  const trimmed = handle.replace(/^@/, "");
  return trimmed.slice(0, 2).toUpperCase();
};

export const Avatar = ({ handle, size = 22 }: AvatarProps): JSX.Element => {
  if (!handle) {
    return (
      <span
        className="avatar unassigned"
        style={{ width: size, height: size, fontSize: size * 0.45 }}
      >
        <Icon name="user" size={size * 0.55} stroke={1.4} />
      </span>
    );
  }
  const hue = hueFor(handle);
  const background = `oklch(66% 0.13 ${hue})`;
  return (
    <span
      className="avatar"
      style={{
        width: size,
        height: size,
        fontSize: size * 0.45,
        background,
      }}
      title={handle}
    >
      {initialsFor(handle)}
    </span>
  );
};
